import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { Quiz } from '@superfunnel/types';
import Badge from '@superfunnel/ui-core/v2/Badge';
import DotStatus from '@superfunnel/ui-core/v2/DotStatus';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { ColumnDef, Table } from '@tanstack/react-table';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import {
  forwardRef,
  ReactElement,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { DashboardContext } from '../contexts/DashboardContext';
import { OverviewContext } from '../contexts/OverviewContext';
import { UserContext } from '../contexts/UserContext';
import { classNames, getAnalyticsAttributes } from '../utils';
import { StatCellValues } from './analytics/StatCellValues';
import {
  CustomDataGrid,
  CustomDataGridHandle,
} from './CustomDataGrid/CustomDataGrid';

export type QuizTableItem = QueryDocumentSnapshot<Quiz>;

export type QuizFunnelsTableHandle = {
  getTableInstance: () => Table<QuizTableItem> | undefined;
};

type QuizStatus = Quiz['status'];

const statusConfig: Record<
  QuizStatus,
  { label: string; variant: 'secondary' | 'success' | 'warning' }
> = {
  draft: { label: 'Draft', variant: 'secondary' },
  active: { label: 'Active', variant: 'success' },
  deleted: { label: 'Deleted', variant: 'secondary' },
};

export function QuizStatusBadge({ status }: { status: QuizStatus }) {
  const config = statusConfig[status] ?? statusConfig.draft;
  return (
    <Badge variant={config.variant} className="font-inter font-normal">
      {config.label}
    </Badge>
  );
}

interface Props {
  items: QuizTableItem[] | undefined;
  actions?: (item: QuizTableItem, index: number) => ReactElement<any>;
  onRowClick?: (quiz: QuizTableItem) => void;
  hideAnalytics?: boolean;
  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: QuizTableItem[]) => void;
}

export const QuizFunnelsTable = forwardRef<QuizFunnelsTableHandle, Props>(
  function QuizFunnelsTable(
    {
      items,
      actions,
      onRowClick,
      hideAnalytics,
      enableRowSelection,
      onRowSelectionChange,
    }: Props,
    ref,
  ) {
    const tableRef = useRef<CustomDataGridHandle<QuizTableItem>>(null);

    const { analyticsCustomEvents, analyticsExternalPixelEvents } =
      useContext(UserContext);
    const { analyticsType } = useContext(DashboardContext);
    const { customRangeAnalytics } = useContext(OverviewContext);

    const getItemMetadataCached = useMemo(() => {
      const _cache = new Map<string, ReturnType<typeof _getMetadata>>();

      const _getMetadata = (quiz: QuizTableItem) => {
        const quizData = quiz.data();

        const _analyticsData = customRangeAnalytics;

        // Analytics are stored by the linkedDynamicPageId (the hidden DynamicPage)
        const analyticsData = _analyticsData
          ? (_analyticsData?.campaign?.[quizData.linkedDynamicPageId] ?? {})
          : undefined;

        const analyticsKey = 'custom';

        const quizExtra = getQuizAttributes(
          quizData.linkedDynamicPageId,
          quizData,
          customRangeAnalytics,
        );

        return {
          quizData,
          quizExtra,
          analyticsData,
          _analyticsData,
          analyticsKey,
        };
      };

      return (quiz: QuizTableItem) => {
        const existing = _cache.get(quiz.id);
        if (!existing) {
          const md = _getMetadata(quiz);
          _cache.set(quiz.id, md);
          return md;
        }

        return existing;
      };
    }, [customRangeAnalytics]);

    const columns: ColumnDef<QuizTableItem>[] = useMemo(() => {
      return [
        {
          id: 'name',
          header: 'Name',
          accessorFn: (row) => row.data().name,
          size: 350,
          cell: ({ row: { original: quiz } }) => (
            <div>
              <div className="font-inter flex items-end gap-1">
                <span className="font-medium text-gray-900">
                  {quiz.data().name}
                </span>
                {!quiz.data().isLive && quiz.data().status !== 'draft' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ExclamationTriangleIcon className="-m-1 h-6 w-6 p-1 text-yellow-400" />
                    </TooltipTrigger>
                    <TooltipContent onClick={(e) => e.stopPropagation()}>
                      <span className="align-middle text-sm font-medium">
                        Quiz has not been published yet
                      </span>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              <div className="text-base-500 font-inter mt-0.5 text-xs">
                /{quiz.data().path}
              </div>
            </div>
          ),
        },
        {
          id: 'id',
          header: 'ID',
          accessorFn: (row) => row.id,
          size: 200,
          cell: ({ row: { original: quiz } }) => (
            <div className="text-gray-500">{quiz.id}</div>
          ),
        },
        {
          id: 'status',
          header: 'Status',
          sortingFn: 'alphanumeric',
          accessorFn: (row) => row.data().status,
          size: 90,
          cell: ({ row: { original } }) => {
            const { quizData } = getItemMetadataCached(original);
            return <QuizStatusBadge status={quizData.status} />;
          },
        },
        {
          id: 'changes',
          header: 'Changes',
          accessorFn: (row) => row.data().hasChangesToPublish ?? false,
          size: 120,
          cell: ({ row: { original } }) => {
            const hasChangesToPublish = original.data().hasChangesToPublish;
            return (
              <div className="font-inter flex items-center gap-2">
                <DotStatus
                  color={hasChangesToPublish ? 'yellow' : 'green'}
                  animate={hasChangesToPublish}
                />
                <span className="text-gray-900">
                  {hasChangesToPublish ? 'Pending' : 'Deployed'}
                </span>
              </div>
            );
          },
        },

        ...(!hideAnalytics
          ? ([
              {
                id: 'visitors',
                header: 'Visitors',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueVisitors?.custom ?? 0;
                },
                size: 120,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => attrs.visitors ?? '-'}
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'conversions',
                header: 'Conversions',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueConversions?.custom ?? 0;
                },
                size: 120,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => attrs.conversions ?? '-'}
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'allConversions',
                header: 'All Conversions',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueConversions?.totalCustom ?? 0;
                },
                size: 140,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => attrs.allConversions ?? '-'}
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'cr',
                header: 'Conv. rate',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  const visitors = analyticsData?.uniqueVisitors?.custom ?? 0;
                  const conversions =
                    analyticsData?.uniqueConversions?.custom ?? 0;
                  const cr = visitors > 0 ? (conversions / visitors) * 100 : 0;
                  return cr;
                },
                size: 170,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) =>
                        attrs.visitors ? `${attrs.cr}%` : '-'
                      }
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'buttonClicks',
                header: 'Button Clicks',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueButtonClicks?.custom ?? 0;
                },
                size: 170,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => attrs.buttonClicks ?? '-'}
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'uniqueLinkClicks',
                header: 'Unique link clicks',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueButtonClicks?.uniqueCustom ?? 0;
                },
                size: 170,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => {
                        const val = attrs.uniqueButtonClicks;
                        const percentageFormatter = new Intl.NumberFormat(
                          'en-US',
                          {
                            style: 'percent',
                            maximumFractionDigits: 0,
                          },
                        );
                        return (
                          <>
                            <span>{val ?? '-'}</span>
                            <span className="ms-1 text-xs opacity-50">
                              (
                              {percentageFormatter.format(
                                (val ?? 0) / (attrs.visitors || 1),
                              )}
                              )
                            </span>
                          </>
                        );
                      }}
                      showAll={false}
                      fill
                    />
                  );
                },
              },
              {
                id: 'formSubmissions',
                header: 'Form Submissions',
                accessorFn: (row) => {
                  const { analyticsData } = getItemMetadataCached(row);
                  return analyticsData?.uniqueFormSubmissions?.custom ?? 0;
                },
                size: 170,
                cell: ({ row: { original } }) => {
                  const { analyticsData, analyticsKey } =
                    getItemMetadataCached(original);
                  return (
                    <StatCellValues
                      analyticsType={analyticsType}
                      analyticsKey={analyticsKey}
                      analyticsData={analyticsData}
                      format={(attrs) => attrs.formSubmissions ?? '-'}
                      showAll={false}
                      fill
                    />
                  );
                },
              },

              ...(analyticsCustomEvents?.map(
                (event) =>
                  ({
                    id: `customEvents-${event.id.replace(/[^a-zA-Z0-9-_]/g, '_')}`,
                    header: `Event: ${event.name}`,
                    accessorFn: (row) => {
                      const { _analyticsData, quizData } =
                        getItemMetadataCached(row);
                      const customEventsAnalyticsData = _analyticsData
                        ? (_analyticsData?.customEvents?.campaign?.[
                            quizData.linkedDynamicPageId
                          ] ?? {})
                        : undefined;
                      return customEventsAnalyticsData?.[event.id]?.custom ?? 0;
                    },
                    size: 250,
                    cell: ({ row: { original } }) => {
                      const {
                        _analyticsData,
                        analyticsData,
                        analyticsKey,
                        quizData,
                      } = getItemMetadataCached(original);
                      return (
                        <StatCellValues
                          analyticsType={analyticsType}
                          analyticsKey={analyticsKey}
                          analyticsData={analyticsData}
                          analyticsCustomEventsData={
                            _analyticsData
                              ? (_analyticsData?.customEvents?.campaign?.[
                                  quizData.linkedDynamicPageId
                                ] ?? {})
                              : undefined
                          }
                          analyticsCustomEvents={analyticsCustomEvents}
                          format={(attrs) => {
                            const percentageFormatter = new Intl.NumberFormat(
                              'en-US',
                              {
                                style: 'percent',
                                maximumFractionDigits: 0,
                              },
                            );
                            return (
                              <>
                                <span>{attrs[event.id] ?? '-'}</span>
                                <span className="ms-1 text-xs opacity-50">
                                  (
                                  {percentageFormatter.format(
                                    (attrs[event.id] ?? 0) /
                                      (attrs.visitors || 1),
                                  )}
                                  )
                                </span>
                              </>
                            );
                          }}
                          showAll={false}
                          fill
                        />
                      );
                    },
                  }) satisfies ColumnDef<QuizTableItem>,
              ) ?? []),

              ...(analyticsExternalPixelEvents?.map(
                (event) =>
                  ({
                    id: `pixelevent-${event.pixelType}-${event.id.replace(/[^a-zA-Z0-9-_]/g, '_')}`,
                    header: `Event: ${event.name}`,
                    accessorFn: (row) => {
                      const { _analyticsData, quizData } =
                        getItemMetadataCached(row);
                      const analyticsExternalPixelsData = _analyticsData
                        ? (_analyticsData?.externalPixels?.[event.pixelType]
                            ?.campaign?.[quizData.linkedDynamicPageId] ?? {})
                        : undefined;
                      return (
                        analyticsExternalPixelsData?.[event.id]?.custom ?? 0
                      );
                    },
                    size: 250,
                    cell: ({ row: { original } }) => {
                      const {
                        _analyticsData,
                        analyticsData,
                        analyticsKey,
                        quizData,
                      } = getItemMetadataCached(original);
                      return (
                        <StatCellValues
                          analyticsType={analyticsType}
                          analyticsKey={analyticsKey}
                          analyticsData={analyticsData}
                          analyticsCustomEventsData={
                            _analyticsData
                              ? (_analyticsData?.externalPixels?.[
                                  event.pixelType
                                ]?.campaign?.[quizData.linkedDynamicPageId] ??
                                {})
                              : undefined
                          }
                          analyticsCustomEvents={analyticsExternalPixelEvents}
                          format={(attrs) => {
                            const percentageFormatter = new Intl.NumberFormat(
                              'en-US',
                              {
                                style: 'percent',
                                maximumFractionDigits: 0,
                              },
                            );
                            return (
                              <>
                                <span>{attrs[event.id] ?? '-'}</span>
                                <span className="ms-1 text-xs opacity-50">
                                  (
                                  {percentageFormatter.format(
                                    (attrs[event.id] ?? 0) /
                                      (attrs.visitors || 1),
                                  )}
                                  )
                                </span>
                              </>
                            );
                          }}
                          showAll={false}
                          fill
                        />
                      );
                    },
                  }) satisfies ColumnDef<QuizTableItem>,
              ) ?? []),
            ] satisfies ColumnDef<QuizTableItem>[])
          : []),

        {
          id: 'tags',
          header: 'Tags',
          accessorKey: 'quizData.tags',
          enableSorting: false,
          size: 350,
          cell: ({ row: { original } }) => {
            return (
              <div>
                <div className="flex flex-wrap gap-1">
                  {original.data().tags?.map((t) => (
                    <Badge key={t.id} className={'font-inter'}>
                      {t.name}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          },
        },
        ...(actions
          ? ([
              {
                id: 'actions',
                header: '',
                accessorKey: 'actions',
                enableSorting: false,
                size: 100,
                cell: ({ row: { original, index } }) =>
                  actions(original, index),
              },
            ] satisfies ColumnDef<QuizTableItem>[])
          : []),
      ] satisfies ColumnDef<QuizTableItem>[];
    }, [
      actions,
      analyticsCustomEvents,
      analyticsExternalPixelEvents,
      analyticsType,
      getItemMetadataCached,
      hideAnalytics,
    ]);

    useImperativeHandle(ref, () => ({
      getTableInstance: () => tableRef.current?.table,
    }));

    const _items = useMemo(() => {
      if (!items) return undefined;
      return [...items];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, customRangeAnalytics]);

    return (
      <CustomDataGrid
        ref={tableRef}
        logicalName="QuizFunnelsTable"
        columns={columns}
        data={_items}
        rowClassName={classNames(onRowClick && 'cursor-pointer')}
        onRowClick={onRowClick}
        enableRowSelection={enableRowSelection}
        onRowSelectionChange={onRowSelectionChange}
        enableSorting
        cellClassName={'font-inter'}
        initialState={{
          columnPinning: {
            right: ['actions'],
          },
          columnVisibility: {
            id: false,
            buttonClicks: false,
            formSubmissions: false,
            uniqueLinkClicks: false,
            allConversions: false,
          },
        }}
      />
    );
  },
);

export function getQuizAttributes(
  linkedDynamicPageId: string,
  quiz: Quiz,
  customRangeAnalytics?: Record<string, any>,
) {
  const quizData = quiz;
  const analyticsData = customRangeAnalytics;
  const analyticsKey = 'custom';

  const analyticsAttrs = getAnalyticsAttributes(
    analyticsKey,
    analyticsData?.campaign?.[linkedDynamicPageId],
  );

  const quizStatus = quizData.status;

  return {
    ...analyticsAttrs,
    quizStatus,
    device: Object.fromEntries(
      Object.entries(
        analyticsData?.campaign?.[linkedDynamicPageId]?.device ?? [],
      ).map(([k, v]) => [k, getAnalyticsAttributes(analyticsKey, v as any)]),
    ),
  };
}
